
import { useState, useEffect, startTransition, Suspense } from 'react';
import { EnhancedChatInterface } from '@/components/chat/enhanced/EnhancedChatInterface';
import { ChatAgentProvider } from '@/components/chat/ChatAgentProvider';
import { ResponseFormattingProvider } from '@/components/chat/formatting/ResponseFormattingContext';
import { Loader } from "@/components/ui/loader";

function ChatErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8">
        <h2 className="text-xl font-semibold mb-4">Something went wrong with the chat</h2>
        <p className="text-gray-600 mb-4">{error.message}</p>
        <button 
          onClick={resetErrorBoundary}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function ChatContent() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time for chat initialization
    const timer = setTimeout(() => {
      startTransition(() => {
        setIsLoading(false);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader size={32} text="Initializing enhanced chat..." />
      </div>
    );
  }

  return (
    <div className="absolute inset-0">
      <ResponseFormattingProvider>
        <ChatAgentProvider>
          <EnhancedChatInterface />
        </ChatAgentProvider>
      </ResponseFormattingProvider>
    </div>
  );
}

const Chat = () => {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader size={32} text="Loading enhanced chat..." />
      </div>
    }>
      <ChatContent />
    </Suspense>
  );
};

export default Chat;
